import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Download, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminApplications = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    departmentId: '',
    intake: '',
  });

  const statusConfig = {
    'New': 'bg-blue-100 text-blue-700',
    'Under Review': 'bg-yellow-100 text-yellow-700',
    'Accepted': 'bg-green-100 text-green-700',
    'Rejected': 'bg-red-100 text-red-700',
  };

  const departments = [
    { id: 'health-allied-sciences', name: 'Health & Allied Sciences' },
    { id: 'business', name: 'Business' },
    { id: 'hotel-tourism', name: 'Hotel & Tourism' },
    { id: 'science-technology', name: 'Science & Technology' },
  ];

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15',
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.intake) params.append('intake', filters.intake);

      const response = await fetch(`${BACKEND_URL}/api/admin/applications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.data);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, filters]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value === 'all' ? '' : value });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', departmentId: '', intake: '' });
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.departmentId) params.append('departmentId', filters.departmentId);
      if (filters.intake) params.append('intake', filters.intake);

      const response = await fetch(`${BACKEND_URL}/api/admin/applications/export/csv?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bit_applications_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const hasActiveFilters = filters.search || filters.status || filters.departmentId || filters.intake;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f]">Applications</h1>
            <p className="text-[#475569]">Manage student applications</p>
          </div>
          <Button onClick={handleExport} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-none shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or application #..."
                  value={filters.search}
                  onChange={handleSearch}
                  className="pl-10"
                />
              </div>

              <Select value={filters.status || 'all'} onValueChange={(v) => handleFilterChange('status', v)}>
                <SelectTrigger className="w-full lg:w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.departmentId || 'all'} onValueChange={(v) => handleFilterChange('departmentId', v)}>
                <SelectTrigger className="w-full lg:w-[200px]">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.intake || 'all'} onValueChange={(v) => handleFilterChange('intake', v)}>
                <SelectTrigger className="w-full lg:w-[150px]">
                  <SelectValue placeholder="Intake" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Intakes</SelectItem>
                  <SelectItem value="March">March</SelectItem>
                  <SelectItem value="September">September</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-[#475569]">
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card className="border-none shadow-md">
          <CardHeader>
            <CardTitle className="text-[#1e3a5f] flex items-center justify-between">
              <span>Applications ({total})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : applications.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#e2e8f0]">
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Application #</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Name</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Email</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Programme</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Intake</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Status</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Date</th>
                        <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                          <td className="py-3 px-4 font-medium text-[#1e3a5f]">{app.applicationNumber}</td>
                          <td className="py-3 px-4 text-[#1e3a5f]">{app.fullName}</td>
                          <td className="py-3 px-4 text-[#475569] text-sm">{app.email}</td>
                          <td className="py-3 px-4 text-[#475569] text-sm">{app.programmeName}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">{app.intake}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={statusConfig[app.status] || 'bg-gray-100'}>
                              {app.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-[#475569] text-sm">
                            {new Date(app.submittedAt).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <Link to={`/admin/applications/${app.id}`}>
                              <Button variant="ghost" size="sm" className="text-[#2563eb]">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#e2e8f0]">
                  <p className="text-[#475569] text-sm">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-[#475569] text-center py-12">No applications found</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
